import React, { useEffect, useRef, useState } from 'react'
import { getZeitrotagSrc } from './snippet'
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'
import './ToolItem.css'

const BrowserFrame = ({ children }) => (
    <div className="tool-item-browser">
        <div className="tool-item-browser-bar">
            <span />
            <span />
            <span />
        </div>
        <div className="tool-item-browser-body">{children}</div>
    </div>
)

const ToolItem = ({
    id,
    name,
    description,
    pageHint,
    snippet,
    previewSrc,
}) => {
    const [copied, setCopied] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(true)
    const copyTimer = useRef(null)
    const previewFrameRef = useRef(null)
    const previewLoadTimer = useRef(null)

    useEffect(() => {
        return () => {
            if (copyTimer.current) clearTimeout(copyTimer.current)
            if (previewLoadTimer.current) clearTimeout(previewLoadTimer.current)
        }
    }, [])

    useEffect(() => {
        if (!previewOpen) {
            setPreviewLoading(true)
            return
        }

        setPreviewLoading(true)

        const iframe = previewFrameRef.current
        if (!iframe) return

        const stopLoading = () => setPreviewLoading(false)

        const onPreviewMessage = (event) => {
            if (!event.data || typeof event.data.source !== 'string') return
            if (event.data.source === 'tools' || event.data.source === 'affordability') {
                stopLoading()
            }
        }

        const bindInnerFrame = () => {
            const doc = iframe.contentDocument
            const inner = doc?.getElementById(`zeitroframe-${id}`)
            if (!inner || inner.dataset.loadBound) return

            inner.dataset.loadBound = '1'
            inner.addEventListener('load', stopLoading)
        }

        const injectTag = () => {
            const doc = iframe.contentDocument
            if (!doc?.body || doc.getElementById('zeitrotag')) return

            doc.body.style.margin = '0'
            doc.body.style.padding = '16px'
            doc.body.style.background = '#fff'

            const script = doc.createElement('script')
            script.id = 'zeitrotag'
            script.src = getZeitrotagSrc(id)
            doc.body.appendChild(script)
        }

        const setupPreview = () => {
            injectTag()
            bindInnerFrame()
        }

        const observer = new MutationObserver(bindInnerFrame)
        const observeBody = () => {
            const doc = iframe.contentDocument
            if (!doc?.body) return
            observer.observe(doc.body, { childList: true, subtree: true })
            bindInnerFrame()
        }

        if (iframe.contentWindow) {
            iframe.contentWindow.addEventListener('message', onPreviewMessage)
        }
        window.addEventListener('message', onPreviewMessage)
        previewLoadTimer.current = setTimeout(stopLoading, 20000)

        setupPreview()
        observeBody()
        iframe.addEventListener('load', setupPreview)

        return () => {
            iframe.removeEventListener('load', setupPreview)
            observer.disconnect()
            if (iframe.contentWindow) {
                iframe.contentWindow.removeEventListener('message', onPreviewMessage)
            }
            window.removeEventListener('message', onPreviewMessage)
            if (previewLoadTimer.current) clearTimeout(previewLoadTimer.current)
        }
    }, [previewOpen, id])

    const handleCopy = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(snippet)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = snippet
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)
            }
            setCopied(true)
            const type = id === 'purchase' ? 'rate_search' : id
            track(STATSIG.PUBLIC_SITE_INTEGRATION.COPY, type, { type })
            if (copyTimer.current) clearTimeout(copyTimer.current)
            copyTimer.current = setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Copy failed', err)
        }
    }

    const preview = <img src={previewSrc} alt={`${name} preview`} />

    const modalPreview = (
        <iframe
            ref={previewFrameRef}
            className="tool-item-live-preview"
            title={`${name} live preview`}
        />
    )

    return (
        <>
            <div className="tool-item" data-tool-id={id}>
                <div className="tool-item-preview">
                    <BrowserFrame>{preview}</BrowserFrame>
                    <button type="button" className="tool-item-live" onClick={() => setPreviewOpen(true)}>
                        View Tool ↗
                    </button>
                </div>
                <div className="tool-item-details">
                    <h2>{name}</h2>
                    <p>{description}</p>
                    <div className="tool-item-snippet-wrap">
                        <div className="tool-item-snippet-label">
                            Paste this into your {pageHint} page
                        </div>
                        <div className="tool-item-snippet-row">
                            <code>{snippet}</code>
                            <button
                                type="button"
                                className={`tool-item-copy${copied ? ' is-copied' : ''}`}
                                onClick={handleCopy}
                            >
                                {copied ? 'Copied ✓' : 'Copy code'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {previewOpen && (
                <div className="tool-item-modal-mask" onClick={() => setPreviewOpen(false)}>
                    <div className="tool-item-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="tool-item-modal-header">
                            <div>
                                <span className="tool-item-modal-title">{name}</span>
                                <span className="tool-item-modal-sub">how it looks on a customer&apos;s website</span>
                            </div>
                            <button type="button" onClick={() => setPreviewOpen(false)} aria-label="Close">
                                ✕
                            </button>
                        </div>
                        <div className="tool-item-modal-body">
                            <div className="tool-item-preview-wrap">
                                {previewLoading && (
                                    <div className="tool-item-preview-loading">
                                        <div className="tool-item-preview-spinner" />
                                        <span>Loading preview…</span>
                                    </div>
                                )}
                                {modalPreview}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ToolItem
